import { setVersions } from "@payping/redux/actions";
import { ConfigProvider, locales } from "@payping/web-components";
import React, { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import semver from "semver";

import { useAsync } from "react-use";
import { webConstants } from "@payping/utils/platformConstants";
import { setDashboardPreference } from "@/redux/actions/dashboardPreference";
import { INIT_ROUTES } from "./router";
import { initSentry } from "./utils";
import { useAppDispatch, useAppSelector } from "./utils/redux";
import { useAppTranslation } from "./utils/useAppTranslation";

initSentry();

const { enUS, faIR } = locales;

const App: React.FC = () => {
  // =================== <REDIRECT USER BASE ON DASHBOARD PREFERENCE> ===================
  //
  const dispatch = useAppDispatch();
  const tokenAuth = useAppSelector((state) => state.auth.token);
  // value is => ("" | "new" | "old")
  const lastDashboardPreference = useAppSelector((state) => state.dashboardPreference.dashboardPreference);

  const urlParams = new URLSearchParams(window.location.search);
  // this value should only be = 'old'  because user chose old Dashboard and now he/she is here
  const newValueOfDashboardPreference = urlParams.get("dashboardPreference");

  useAsync(async () => {
    if (newValueOfDashboardPreference === "old") await dispatch(setDashboardPreference(newValueOfDashboardPreference));
    else if (newValueOfDashboardPreference == null && lastDashboardPreference === "new")
      window.location.href = `https://newapp.${webConstants.MAIN_HOST}/oauth_callback?dashboardPreference=new#access_token=${tokenAuth}`;
  }, []);

  // =================== </> ===================

  const locale = useAppSelector((state) => state.settings.locale);
  const direction = useAppSelector((state) => state.settings.direction);
  const { i18n } = useAppTranslation();
  const defaultPath = INIT_ROUTES.initApp.path;

  const configLocale = {
    en: enUS,
    fa: faIR,
  };

  useEffect(() => {
    i18n.changeLanguage(locale);
    // Get the new service worker from swUrl
    const swUrl = `/service-worker.js?v=${Math.random()}`;
    fetch(swUrl, {
      cache: "no-store",
      headers: { "Service-Worker": "script" },
    })
      .then((response) => {
        const contentType = response.headers.get("content-type");
        if (response.status === 404 || (contentType != null && contentType.indexOf("javascript") === -1)) {
          // No service worker found. Probably a different app. Reload the page.
          navigator.serviceWorker.ready.then((registration) => {
            registration.unregister().then(() => {
              window.location.reload();
            });
          });
        } else {
          response.text().then((res) => {
            // This is the old version of the dashboard which belongs to the old service worker (current one)
            const currentVersion = process.env.REACT_APP_VERSION;
            // Extract the lastSupportedVersion from the new service-worker
            const supportedVersion = res.match(/console\.log\(['"]lastSupportedVersion[:'", ]*(.*?)['" ]\)/)?.[1];
            // Extract the latestVersion from the new service-worker
            const latestVersion = res.match(/console\.log\(['"]latestVersion[:'", ]*(.*?)['" ]\)/)?.[1];
            dispatch(
              setVersions({
                current: currentVersion,
                latest: latestVersion,
                supported: supportedVersion,
              })
            );
            if (
              currentVersion &&
              supportedVersion &&
              latestVersion &&
              semver.lt(currentVersion, latestVersion) &&
              semver.lt(currentVersion, supportedVersion)
            ) {
              navigator.serviceWorker.ready.then((registration) => {
                registration.unregister().then(() => {
                  window.location.reload();
                });
              });
            }
          });
        }
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log("No internet connection found. App is running in offline mode.");
      });
  }, [dispatch, i18n, locale]);

  return (
    <ConfigProvider direction={direction} locale={configLocale[locale]} localeName={locale}>
      <Routes>
        {Object.values(INIT_ROUTES).map(({ component: Component, path: componentPath }, index) => (
          <Route path={componentPath} key={index} element={<Component />} />
        ))}
        <Route path={"*"} element={<Navigate to={defaultPath} replace />} />
      </Routes>
    </ConfigProvider>
  );
};

export default App;
